/*
 * Copyright (c) 2023 Bolmate B.V.
 */

import React, {ForwardedRef, forwardRef, MouseEventHandler, useImperativeHandle, useState} from 'react';
import {navigate} from '@dvrd/dvr-controls';
import {ActivateHandle} from "../../../util/interfaces";
import BolReturn from "../../../models/bolReturnModel";
import ReturnDetails from "./returnDetails";

interface Props {
    onClickCredit: (bolReturn: BolReturn) => MouseEventHandler;
    onClickSend: (invoiceId: string) => MouseEventHandler;
    onClickBol: MouseEventHandler;
}

export interface ReloadableActivateHandle<T> extends ActivateHandle<T> {
    reload: (param: T) => void;
}

function ReturnDetailsController(props: Props, ref: ForwardedRef<ReloadableActivateHandle<BolReturn>>) {
    const {onClickCredit, onClickSend, onClickBol} = props;
    const [active, setActive] = useState(false),
        [bolReturn, setBolReturn] = useState(new BolReturn()),
        [loading, setLoading] = useState(false);

    function onReload(_bolReturn: BolReturn) {
        if (active && _bolReturn.id === bolReturn.id) setBolReturn(_bolReturn);
        if (loading) setLoading(false);
    }

    function onActivate(bolReturn: BolReturn) {
        setBolReturn(bolReturn);
        setActive(true);
        setLoading(false);
    }

    function onClose() {
        setActive(false);
        setBolReturn(new BolReturn());
        setLoading(false);
    }

    function onClickInvoice(invoiceId: string) {
        return function () {
            onClose();
            navigate(`/factuur-details?id=${invoiceId}`);
        }
    }

    function _onClickCredit(evt: React.MouseEvent) {
        setLoading(true);
        onClickCredit(bolReturn)(evt);
    }

    function _onClickSend(invoiceId: string) {
        return function (evt: React.MouseEvent) {
            setLoading(true);
            onClickSend(invoiceId)(evt);
        }
    }

    useImperativeHandle(ref, () => ({activate: onActivate, reload: onReload}));

    return (
        <ReturnDetails onClose={onClose} onClickInvoice={onClickInvoice} onClickSend={_onClickSend}
                       onClickCredit={_onClickCredit} onClickBol={onClickBol} active={active} bolReturn={bolReturn}
                       loading={loading}/>
    )
}

export default forwardRef<ReloadableActivateHandle<BolReturn>, Props>(ReturnDetailsController);